import React, { useState, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const CostGrid = ({ data, costKeys, valueFields, onSave, saving }) => {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const [editData, setEditData] = useState(() => JSON.parse(JSON.stringify(data)));
  const [editingCell, setEditingCell] = useState(null);
  const [dirty, setDirty] = useState(false);

  const handleCellChange = useCallback((rowIdx, costKey, field, value) => {
    setEditData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (updated[rowIdx] && updated[rowIdx][costKey]) {
        updated[rowIdx][costKey][field] = Number(value) || 0;
      }
      return updated;
    });
    setDirty(true);
  }, []);

  const handleSave = () => {
    onSave(editData);
    setDirty(false);
  };

  // Build row labels from star/level
  const rows = editData.map((row, i) => ({
    index: i,
    label: row.star != null ? `S${row.star} L${row.level}` : `L${row.level}`,
  }));

  const inputCls = `w-full px-1 py-0.5 text-xs text-center border rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
    dark ? 'bg-[#121212] border-[#444] text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  return (
    <div className="space-y-3">
      {dirty && (
        <div className="flex items-center justify-between">
          <span className={`text-sm ${dark ? 'text-[#FF9800]' : 'text-indigo-600'}`}>
            Unsaved changes
          </span>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${
              dark ? 'bg-[#FF9800] hover:bg-[#F57C00]' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      <div className={`overflow-x-auto rounded-lg border ${dark ? 'border-[#333]' : 'border-gray-200'}`}>
        <table className="text-xs">
          <thead>
            <tr className={dark ? 'bg-[#1A1A1A]' : 'bg-gray-50'}>
              <th className={`px-3 py-2 text-left font-medium sticky left-0 z-10 ${dark ? 'bg-[#1A1A1A] text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                Level
              </th>
              {costKeys.map(ck => (
                <th key={ck} colSpan={valueFields.length}
                  className={`px-2 py-2 text-center font-medium border-l ${dark ? 'border-[#333] text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                  {ck.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
            <tr className={dark ? 'bg-[#1A1A1A]' : 'bg-gray-50'}>
              <th className={`px-3 py-1 sticky left-0 z-10 ${dark ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}></th>
              {costKeys.map(ck =>
                valueFields.map(vf => (
                  <th key={`${ck}-${vf}`}
                    className={`px-2 py-1 text-center font-normal border-l ${dark ? 'border-[#333] text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                    {vf}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ index: rowIdx, label }) => (
              <tr key={rowIdx} className={`border-t ${dark ? 'border-[#333]' : 'border-gray-100'}`}>
                <td className={`px-3 py-1.5 font-medium whitespace-nowrap sticky left-0 z-10 ${
                  dark ? 'bg-[#121212] text-gray-300' : 'bg-white text-gray-700'
                }`}>
                  {label}
                </td>
                {costKeys.map(ck =>
                  valueFields.map(vf => {
                    const cellId = `${rowIdx}-${ck}-${vf}`;
                    const val = editData[rowIdx]?.[ck]?.[vf] ?? 0;
                    return (
                      <td key={cellId}
                        className={`px-1 py-1 border-l ${dark ? 'border-[#333]' : 'border-gray-100'}`}
                        onClick={() => setEditingCell(cellId)}>
                        {editingCell === cellId ? (
                          <input
                            type="number"
                            className={inputCls}
                            defaultValue={val}
                            autoFocus
                            onBlur={(e) => {
                              handleCellChange(rowIdx, ck, vf, e.target.value);
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleCellChange(rowIdx, ck, vf, e.target.value);
                                setEditingCell(null);
                              }
                              if (e.key === 'Escape') setEditingCell(null);
                            }}
                          />
                        ) : (
                          <span className={`block text-center cursor-pointer px-1 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-[#333] tabular-nums ${
                            val === 0 ? (dark ? 'text-gray-600' : 'text-gray-300') : (dark ? 'text-gray-300' : 'text-gray-700')
                          }`}>
                            {val}
                          </span>
                        )}
                      </td>
                    );
                  })
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CostGrid;
